<template>
	<div>
		<v-snackbar v-model="meetingLocationAddedNotif" auto-height top>
			{{ meetingLocationAddedNotifText }}
			<v-btn color="pink" flat @click="meetingLocationAddedNotif = false"> Close </v-btn>
		</v-snackbar>

		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<v-form>
			<v-stepper v-model="e1">
				<v-stepper-header>
					<v-stepper-step :complete="e1 > 1" step="1">Review Type</v-stepper-step>

					<v-divider></v-divider>
					<v-stepper-step :complete="e1 > 1" step="2">Review Detail</v-stepper-step>

					<v-divider></v-divider>
					<v-stepper-step :complete="e1 > 2" step="3">Discussion Points</v-stepper-step>

					<v-divider></v-divider>
					<v-stepper-step :complete="e1 > 3" step="4">Summary</v-stepper-step>
				</v-stepper-header>

				<v-stepper-items>
					<v-stepper-content step="1">
						<v-card class="mb-4" flat>
							<v-card-text class="">
								<v-layout row wrap>
									<v-flex md12 xl6>
										<h2 class="mb-4">Choose from a Template</h2>

										<v-list two-line="" subheader>
											<v-list-tile style="background-color: #e3f2fd" avatar @click="stepOneContinue(0)">
												<v-list-tile-avatar>
													<v-icon class="primary white--text">person</v-icon>
												</v-list-tile-avatar>

												<v-list-tile-content>
													<v-list-tile-title>Default</v-list-tile-title>
													<v-list-tile-sub-title>Based on the employee's Position Description <i>(recommended)</i></v-list-tile-sub-title>
												</v-list-tile-content>

												<v-list-tile-action>
													<v-icon color="grey lighten-1">chevron_right</v-icon>
												</v-list-tile-action>
											</v-list-tile>
										</v-list>

										<performance-review-template-selector :templates="templates" @onTemplateSelect="onTemplateSelect" />
									</v-flex>
									<v-flex lg6>
										<!-- <h2>Choose from a Template</h2> -->
									</v-flex>
								</v-layout>
							</v-card-text>
						</v-card>
					</v-stepper-content>

					<v-stepper-content step="2">
						<v-flex lg12 class="">
							<v-card class="mb-4" flat>
								<v-card-text class="">
									<v-select
										v-model="form.employee"
										prepend-icon="person"
										:items="allowedEmployees"
										:error-messages="employeeErrors"
										item-text="fullName"
										return-object
										label="Employee"
										required
										@change="onEmployeeSelect"
										@blur="$v.form.employee.$touch()"
									></v-select>

									<v-alert v-if="form.performanceReviewType === 0 && form.employee && !this.isLoadingEmployee && !employeePositionUsedForReview" class="text-xs-left mb-4" type="warning" :value="true" outline>
										<router-link :to="{ name: 'employee', params: { id: form.employee.id } }">{{ form.employee.fullName }}</router-link> currently has no positions available for use in a performance review. <br /><span
											style="font-style: italic"
											>Please ensure that any positions setup are approved by the manager and acknowledged by the employee.</span
										>
									</v-alert>

									<v-select
										v-model="form.reviewer"
										prepend-icon="person"
										:items="otherEmployees"
										:error-messages="reviewerErrors"
										item-text="fullName"
										return-object
										label="Reviewer"
										required
										@change="$v.form.reviewer.$touch()"
										@blur="$v.form.reviewer.$touch()"
									></v-select>

									<v-menu
										ref="periodFromUTCMenu"
										v-model="form.periodFromUTC.menu"
										:close-on-content-click="false"
										:nudge-right="40"
										:return-value.sync="form.periodFromUTC.date"
										lazy
										transition="scale-transition"
										offset-y
										full-width
										min-width="290px"
									>
										<v-text-field slot="activator" v-model="periodFromFormatted" prepend-icon="date_range" label="Period From" readonly :error-messages="periodFromUTCErrors"></v-text-field>
										<v-date-picker v-model="form.periodFromUTC.date" @input="$refs.periodFromUTCMenu.save(form.periodFromUTC.date)"> </v-date-picker>
									</v-menu>

									<v-menu
										ref="periodToUTCMenu"
										v-model="form.periodToUTC.menu"
										:close-on-content-click="false"
										:nudge-right="40"
										:return-value.sync="form.periodToUTC.date"
										lazy
										transition="scale-transition"
										offset-y
										full-width
										min-width="290px"
									>
										<v-text-field slot="activator" v-model="periodToFormatted" prepend-icon="date_range" label="Period To" readonly :error-messages="periodToUTCErrors"></v-text-field>
										<v-date-picker v-model="form.periodToUTC.date" @input="$refs.periodToUTCMenu.save(form.periodToUTC.date)"> </v-date-picker>
									</v-menu>

									<v-layout row wrap>
										<v-flex sm6>
											<v-menu
												ref="scheduledDateUTCMenu"
												v-model="form.scheduledDateUTC.menu"
												:close-on-content-click="false"
												:nudge-right="40"
												:return-value.sync="form.scheduledDateUTC.date"
												lazy
												transition="scale-transition"
												offset-y
												full-width
												min-width="290px"
											>
												<v-text-field slot="activator" v-model="scheduledDateFormatted" prepend-icon="date_range" label="Scheduled Date" readonly :error-messages="scheduledDateUTCErrors"></v-text-field>
												<v-date-picker v-model="form.scheduledDateUTC.date" @input="$refs.scheduledDateUTCMenu.save(form.scheduledDateUTC.date)"> </v-date-picker>
											</v-menu>
										</v-flex>

										<v-flex sm6>
											<v-dialog ref="scheduledTimeMenu" v-model="form.scheduledTime.menu" :return-value.sync="form.scheduledTime.time" persistent lazy full-width width="290px">
												<template v-slot:activator="{ on }">
													<v-text-field v-model="form.scheduledTime.time" label="Scheduled Time" prepend-icon="access_time" readonly v-on="on"></v-text-field>
												</template>
												<v-time-picker v-if="form.scheduledTime.menu" v-model="form.scheduledTime.time" full-width>
													<v-spacer></v-spacer>
													<v-btn flat color="primary" @click="form.scheduledTime.menu = false">Cancel</v-btn>
													<v-btn flat color="primary" @click="$refs.scheduledTimeMenu.save(form.scheduledTime.time)">OK</v-btn>
												</v-time-picker>
											</v-dialog>
										</v-flex>
									</v-layout>

									<v-select v-model="form.meetingLocation" clearable prepend-icon="location_on" :items="meetingLocations" label="Meeting Location" item-text="name" item-value="id" return-object required> </v-select>
									<div class="text-xs-left" style="margin-top: -12px; margin-left: 20px">
										<v-btn flat small color="primary" @click="onAddMeetingLocation">Add new</v-btn>
									</div>
								</v-card-text>
							</v-card>
						</v-flex>

						<v-btn flat @click="e1 = 1"><v-icon left>chevron_left</v-icon> Back</v-btn>

						<v-btn color="primary" @click="stepTwoContinue" :loading="isLoadingEmployee" :disabled="isLoadingEmployee"> Continue<span slot="loader">Loading...</span> </v-btn>
					</v-stepper-content>

					<v-stepper-content step="3">
						<div v-if="form.performanceReviewType === 0">
							<v-layout row wrap>
								<v-flex lg6>
									<v-toolbar flat dense class="mr-2">
										<v-toolbar-title class="subheading"> Employee </v-toolbar-title>
										<v-spacer></v-spacer>
									</v-toolbar>
									<v-list two-line v-if="form.employee">
										<v-list-tile inactive>
											<v-list-tile-action>
												<v-avatar size="36px">
													<img :src="getAvatar(form.employee.id)" alt="avatar" class="avatar-img" />
												</v-avatar>
											</v-list-tile-action>

											<v-list-tile-content>
												<v-list-tile-title>
													<span v-if="employeePositionUsedForReview">{{ form.employee.fullName }}</span>
													<router-link v-else :to="{ name: 'employee', params: { id: form.employee.id } }">{{ form.employee.fullName }}</router-link>
												</v-list-tile-title>
												<v-list-tile-sub-title>
													<div v-if="employeePositionUsedForReview">
														<router-link :to="{ name: 'employeePosition', params: { employeeId: selectedEmployee.id, id: employeePositionUsedForReview.id } }" target="_blank">
															{{ employeePositionUsedForReview.title }}
														</router-link>
														<v-icon small> open_in_new </v-icon>
													</div>
													<div v-else>No positions currently available for use in performance review</div>
												</v-list-tile-sub-title>
											</v-list-tile-content>
										</v-list-tile>
									</v-list>
								</v-flex>
								<v-flex lg6 v-if="isCondensedPerformanceReviewsEnabled">
									<v-card class="ml-3 mb-1">
										<v-toolbar flat dense color="info" dark>
											<v-toolbar-title class="subheading"><v-icon class="mr-2">tune</v-icon>Options </v-toolbar-title>
											<v-spacer></v-spacer>
										</v-toolbar>
										<v-card-text class="py-0">
											<v-radio-group v-model="performanceReviewOptions.condensed">
												<v-radio :value="0">
													<template v-slot:label>
														<div class="mb-1">
															Full Review<v-chip class="ml-2" color="info" dark small>Recommended</v-chip>
															<div class="caption">Includes all KPI's and Key Duties from the position description.</div>
														</div>
													</template>
												</v-radio>
												<v-radio :value="1">
													<template v-slot:label>
														<div>
															Condensed Review
															<div class="caption">A streamlined option to reduce the evaluation of key duties and deliverables in the position description (review is conducted using high-level categories only)</div>
														</div>
													</template></v-radio
												>
											</v-radio-group>
											<div v-if="performanceReviewOptions.includeKPIs && performanceReviewOptions.condensed" class="ml-4">
												<v-switch class="condense-switch" v-model="performanceReviewOptions.condenseKPIs">
													<template v-slot:label>
														<div>
															Also Condense KPI's
															<div class="caption">In condensed mode the key duties are condensed. Select this option if you also want to condense the KPIs.</div>
														</div>
													</template>
												</v-switch>
											</div>
										</v-card-text>
									</v-card>
								</v-flex>
							</v-layout>

							<div v-show="form.reviewItems && form.reviewItems.length > 10" class="mt-2">
								<v-btn flat @click="e1 = 2"><v-icon left>chevron_left</v-icon> Back</v-btn>
								<v-btn color="primary" @click="stepThreeContinue"> Continue </v-btn>
							</div>

							<v-flex lg12>
								<v-card class="mb-4 mt-3" flat>
									<v-toolbar flat dense>
											<v-toolbar-title class="subheading"><v-icon class="mr-2">mdi-comment-text-multiple-outline</v-icon>Discussion Points<a href="#" @click.prevent.stop="showPerformanceReviewOptionsDialog = true" class="ml-2 caption">Configure</a></v-toolbar-title>
											<v-spacer></v-spacer>
										</v-toolbar>
									<v-card-text>
										<performance-review-item-list
											:performance-review-items="form.reviewItems"
											:show-category="!performanceReviewOptions.condensed || !performanceReviewOptions.condenseKPIs"
											@onEditItem="onEditItem"
											@onDeleteItem="onDeleteItem"
										>
										</performance-review-item-list>

										<employee-position-item-editor v-if="editItemDialog" :performance-review="true" :position-item="editedItem" :type="itemDialogType" @save="saveItem" @closeEditor="closeItemEditor">
										</employee-position-item-editor>

										<add-position-item-multi v-if="addPositionItemDialog" :performance-review="true" :type="null" @save="savePositionItemAddMulti" @closeEditor="closeItemEditor"> </add-position-item-multi>
									</v-card-text>
									<v-card-actions>
										<v-spacer></v-spacer>
										<v-btn flat @click="onAddItem"><v-icon left>add</v-icon>Add Items</v-btn>
									</v-card-actions>
								</v-card>
							</v-flex>
						</div>

						<div v-else>
							<performance-review-item-builder v-if="e1 === 3" :review-items="form.reviewItems" :edit="true" />
						</div>

						<v-btn flat @click="e1 = 2"><v-icon left>chevron_left</v-icon> Back</v-btn>

						<v-btn color="primary" @click="stepThreeContinue"> Continue </v-btn>
					</v-stepper-content>

					<v-stepper-content step="4" v-if="e1 === 4">
						<v-card-text class="pa-0">
							<v-container grid-list-xl fluid class="pt-0">
								<v-layout row wrap>
									<v-flex md6>
										<v-card flat class="mb-4">
											<v-toolbar flat dense cad>
												<v-toolbar-title class="subheading"> Review Detail </v-toolbar-title>
												<v-spacer></v-spacer>
											</v-toolbar>

											<v-divider></v-divider>
											<v-list two-line>
												<v-list-tile inactive>
													<v-list-tile-action>
														<v-avatar size="36px">
															<img :src="getAvatar(form.employee.id)" alt="avatar" class="avatar-img" />
														</v-avatar>
													</v-list-tile-action>

													<v-list-tile-content>
														<v-list-tile-title>{{ form.employee.fullName }}</v-list-tile-title>
														<v-list-tile-sub-title>Employee</v-list-tile-sub-title>
													</v-list-tile-content>
												</v-list-tile>

												<v-divider inset></v-divider>

												<v-list-tile inactive>
													<v-list-tile-action>
														<v-avatar size="36px">
															<img :src="getAvatar(form.reviewer.id)" alt="avatar" class="avatar-img" />
														</v-avatar>
													</v-list-tile-action>

													<v-list-tile-content>
														<v-list-tile-title>{{ form.reviewer.fullName }}</v-list-tile-title>
														<v-list-tile-sub-title>Reviewer</v-list-tile-sub-title>
													</v-list-tile-content>
												</v-list-tile>

												<v-divider inset></v-divider>

												<v-list-tile inactive>
													<v-list-tile-action>
														<v-icon color="indigo">date_range</v-icon>
													</v-list-tile-action>

													<v-list-tile-content>
														<v-list-tile-title>{{ form.periodFromUTC.date | formatDate('MMM YYYY') }} - {{ form.periodToUTC.date | formatDate('MMM YYYY') }}</v-list-tile-title>
														<v-list-tile-sub-title>Review Period</v-list-tile-sub-title>
													</v-list-tile-content>
												</v-list-tile>

												<v-divider inset></v-divider>

												<v-list-tile inactive>
													<v-list-tile-action>
														<v-icon color="indigo">date_range</v-icon>
													</v-list-tile-action>

													<v-list-tile-content>
														<v-list-tile-title v-if="scheduledDateTimeSpecified">{{ scheduledDateUtcFormatted | formatDateTime() }}</v-list-tile-title>
														<v-list-tile-title v-else>{{ scheduledDateUtcFormatted | formatDate() }} </v-list-tile-title>
														<v-list-tile-sub-title>Scheduled Date & Time<span v-if="!scheduledDateTimeSpecified" style="font-style: italic"> - No time has been specified</span></v-list-tile-sub-title>
													</v-list-tile-content>
												</v-list-tile>

												<v-divider inset></v-divider>

												<v-list-tile inactive>
													<v-list-tile-action>
														<v-icon color="indigo">location_on</v-icon>
													</v-list-tile-action>

													<v-list-tile-content>
														<v-list-tile-title>{{ meetingLocation }}</v-list-tile-title>
														<v-list-tile-sub-title>Meeting Location</v-list-tile-sub-title>
													</v-list-tile-content>
												</v-list-tile>
											</v-list>
										</v-card>
									</v-flex>
									<v-flex md6>
										<v-card flat class="ma-0">
											<v-toolbar flat dense cad dark color="info">
												<v-toolbar-title class="subheading"> <v-icon class="mr-1">mdi-email</v-icon> Notifications </v-toolbar-title>
												<v-spacer></v-spacer>
											</v-toolbar>

											<v-divider></v-divider>
											<v-card-text>
												<v-switch prepend-icon="mdi-email" v-model="form.sendEmails" label="Send emails" hint="This will send out email notifications with a link to fill out the review." persistent-hint></v-switch>
												<v-switch prepend-icon="date_range" v-model="form.sendMeetingRequests" label="Send calendar invites" :hint="calendarInvitesNotificationText" persistent-hint></v-switch>
											</v-card-text>
										</v-card>
									</v-flex>
								</v-layout>
							</v-container>
						</v-card-text>

						<v-btn flat @click="e1 = 3"><v-icon left>chevron_left</v-icon> Back</v-btn>

						<v-btn class="action-btn" :disabled="isSaving" :loading="isSaving" @click="submit">Save Performance Review <span slot="loader">Saving...</span></v-btn>
					</v-stepper-content>
				</v-stepper-items>
			</v-stepper>
		</v-form>
		<performance-review-options-dialog 
		v-if="showPerformanceReviewOptionsDialog" 
		:dialog="showPerformanceReviewOptionsDialog" 
		:options="performanceReviewOptions" 
		@onConfirm="onPerformanceReviewOptionsUpdated"
		@onCancel="showPerformanceReviewOptionsDialog = false" />
		<meeting-location-editor :dialog="meetingLocationEditorDialog" title="Add New Meeting Location" @save="saveMeetingLocation" @closeEditor="meetingLocationEditorDialog = false"> </meeting-location-editor>
	</div>
</template>

<script>
	import moment from 'moment';
	import typeFriendlyMixin from '@/mixins/typeFriendlyMixin';
	import { validationMixin } from 'vuelidate';
	import { required } from 'vuelidate/lib/validators';
	import PerformanceReviewItemList from '@/components/performanceReview/PerformanceReviewItemList';
	import EmployeePositionItemEditor from '@/components/employee/EmployeePositionItemEditor';
	import AddPositionItemMulti from '@/components/AddPositionItemMulti';
	import MeetingLocationEditor from '@/components/settings/MeetingLocationEditor';
	import PerformanceReviewItemBuilder from '@/components/performanceReview/PerformanceReviewItemBuilder';
	import PerformanceReviewTemplateSelector from '@/components/performanceReview/PerformanceReviewTemplateSelector';
	import PerformanceReviewOptionsDialog from '@/components/performanceReview/PerformanceReviewOptionsDialog';
	import avatarMixin from '@/mixins/avatarMixin';
	import FeatureFlag from '@/enums/featureFlag';


	export default {
		components: {
			PerformanceReviewItemList,
			EmployeePositionItemEditor,
			AddPositionItemMulti,
			MeetingLocationEditor,
			PerformanceReviewItemBuilder,
			PerformanceReviewTemplateSelector,
			PerformanceReviewOptionsDialog
		},
		props: {
			loggedInEmployee: {
				required: true
			},
			employees: {
				required: true
			},
			defaultDiscussionPoints: {
				required: true
			},
			coreValues: {
				required: true
			},
			performanceReview: {
				type: Object,
				default: () => ({
					id: null,
					periodFromUTC: null,
					periodToUTC: null,
					scheduledDateUTC: null,
					reviewer: null,
					employee: null,
					reviewItems: [],
					employeeSubmit: null,
					reviewerSubmit: null,
					status: null
				})
			},
			meetingLocations: {
				type: Array,
				required: true
			},
			templates: {
				type: Array,
				required: true
			},
			isSaving: {
				type: Boolean,
				required: false
			},
			employeeId: {
				type: String,
				required: false
			}
		},
		data() {
			return {
				e1: 0,
				form: {
					id: this.performanceReview.id,
					periodFromUTC: {
						date: this.$options.filters.formatDate(this.performanceReview.periodFromUTC, 'YYYY-MM-DD'),
						menu: false
					},
					periodToUTC: {
						date: this.$options.filters.formatDate(this.performanceReview.periodToUTC, 'YYYY-MM-DD'),
						menu: false
					},
					scheduledDateUTC: {
						date: this.$options.filters.formatDate(this.performanceReview.scheduledDateUTC, 'YYYY-MM-DD'),
						menu: false
					},
					scheduledTime: {
						time: this.$options.filters.formatDate(this.performanceReview.scheduledDateUTC, 'HH:mm'),
						menu: false
					},
					reviewer: this.performanceReview.reviewer,
					employee: this.performanceReview.employee,
					reviewItems: this.performanceReview.reviewItems,
					employeeSubmit: this.performanceReview.employeeSubmit,
					reviewerSubmit: this.performanceReview.reviewerSubmit,
					status: this.performanceReview.status,
					meetingLocation: null,
					performanceReviewType: null,
					performanceReviewTemplate: null,
					sendMeetingRequests: true,
					sendEmails: true
				},
				editItemDialog: false,
				editedItemIndex: -1,
				editedItem: undefined,
				itemDialogType: null,
				selectedEmployee: null,
				addPositionItemDialog: false,
				meetingLocationEditorDialog: false,
				meetingLocationAddedNotifText: null,
				meetingLocationAddedNotif: false,
				isLoadingEmployee: false,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				employeePositionUsedForReview: null,
				performanceReviewOptions: {
					condensed: 0,
					condenseKPIs: false,
					includeKPIs: true,
					includeKeyDuties: true,
					includePersonSpecification: true,
					includeCoreValues: true
				},
				showPerformanceReviewOptionsDialog: false
			};
		},
		created() {
			// Pre-populate
			if (this.employeeId) {
				this.form.performanceReviewType = 0;
				this.form.employee = this.employees.find((x) => x.id === this.employeeId);
				this.onEmployeeSelect(this.form.employee);
				this.stepOneContinue(0, undefined, true);
			}
		},
		watch: {
			performanceReviewOptions: {
				deep: true,
				handler() {
					this.populateReviewItemsFromEmployee();
				}	
			}
			// prFullCondensedType() {
			// 	this.populateReviewItemsFromEmployee();
			// },
			// condenseKPIs() {
			// 	this.populateReviewItemsFromEmployee();
			// },
			// excludePersonSpecificationItems() {
			// 	this.populateReviewItemsFromEmployee();
			// }
		},
		computed: {
			isCondensedPerformanceReviewsEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.CondensedPerformanceReviews);
			},
			calendarInvitesNotificationText() {
				let result = `This will send out calendar invites for a meeting on `;
				if (this.scheduledDateTimeSpecified) return (result += `${this.$options.filters.formatDateTime(this.scheduledDateUtcFormatted)}`);
				else {
					return (result += `${this.$options.filters.formatDate(this.scheduledDateUtcFormatted)}`);
				}
			},
			allowedEmployees() {
				if (this.loggedInEmployee !== null) {
					let result = this.employees.slice(0);

					if (this.loggedInEmployee.isAdmin) {
						return result;
					}

					if (this.loggedInEmployee.underlings !== null) {
						result = result.filter((x) => {
							return this.loggedInEmployee.underlings.includes(x.id) || x.id == this.loggedInEmployee.id;
						});
					} else {
						result = result.filter((x) => {
							return x.id == this.loggedInEmployee.id;
						});
					}

					return result;
				}

				return null;
			},
			otherEmployees() {
				return this.$props.employees;
			},
			employeeErrors() {
				const errors = [];
				if (!this.$v.form.employee.$dirty) return errors;
				!this.$v.form.employee.required && errors.push('Employee is required.');
				return errors;
			},
			reviewerErrors() {
				const errors = [];
				if (!this.$v.form.reviewer.$dirty) return errors;
				!this.$v.form.reviewer.required && errors.push('Reviewer is required.');
				return errors;
			},
			periodFromUTCErrors() {
				const errors = [];
				if (!this.$v.form.periodFromUTC.date.$dirty) return errors;
				!this.$v.form.periodFromUTC.date.required && errors.push('Period From is required.');
				return errors;
			},
			periodToUTCErrors() {
				const errors = [];
				if (!this.$v.form.periodToUTC.date.$dirty) return errors;
				!this.$v.form.periodToUTC.date.required && errors.push('Period To is required.');
				return errors;
			},
			scheduledDateUTCErrors() {
				const errors = [];
				if (!this.$v.form.scheduledDateUTC.date.$dirty) return errors;
				!this.$v.form.scheduledDateUTC.date.required && errors.push('Scheduled Date is required.');
				return errors;
			},
			scheduledDateFormatted() {
				return this.$options.filters.formatDate(this.form.scheduledDateUTC.date);
			},
			periodFromFormatted() {
				return this.$options.filters.formatDate(this.form.periodFromUTC.date);
			},
			periodToFormatted() {
				return this.$options.filters.formatDate(this.form.periodToUTC.date);
			},
			scheduledDateTimeSpecified() {
				return this.form.scheduledTime.time != null;
			},
			meetingLocation() {
				if (this.form.meetingLocation != null) {
					return this.form.meetingLocation.name;
				} else {
					return 'N/A';
				}
			},
			scheduledDateUtcFormatted() {
				return moment(`${this.form.scheduledDateUTC.date} ${this.form.scheduledTime.time}`, 'YYYY-MM-DD HH:mm');
			}
		},
		mixins: [validationMixin, typeFriendlyMixin, avatarMixin],
		validations: {
			form: {
				employee: { required },
				reviewer: { required },
				periodFromUTC: { date: { required } },
				periodToUTC: { date: { required } },
				scheduledDateUTC: { date: { required } }
			}
		},

		methods: {
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			stepOneContinue(performanceReviewType, performanceReviewTemplate, preserveEmployee) {
				// Default
				if (performanceReviewType === 0) {
					if (!preserveEmployee) {
						this.form.employee = null;
						this.form.reviewItems = [];
					}
				} else {
					if (performanceReviewTemplate != null) {
						this.form.performanceReviewTemplate = performanceReviewTemplate;
						this.populateReviewItemsFromTemplate(performanceReviewTemplate);
					} else {
						// Custom (blank)
						this.form.reviewItems = [];
						this.form.reviewItems.push({
							id: this.newGuid(),
							category: null,
							item: 'Example discussion point',
							type: 99,
							outcomes: [],
							group: null,
							rankType: 0
						});
						this.form.reviewItems.push({
							id: this.newGuid(),
							category: null,
							item: 'Example discussion point with rating',
							type: 99,
							outcomes: [],
							group: null,
							rankType: 1
						});
					}
				}

				this.form.performanceReviewType = performanceReviewType;

				this.e1 = 2;
			},
			stepTwoContinue() {
				if (this.isLoadingEmployee) {
					return;
				}

				this.$v.form.$touch();

				if (this.$v.form.$invalid) {
				} else {
					this.e1 = 3;
				}
			},
			stepThreeContinue() {
				this.$v.form.$touch();

				if (this.$v.form.$invalid) {
				} else {
					this.e1 = 4;
				}
			},
			onTemplateSelect(template) {
				this.stepOneContinue(1, template);
			},
			cleanReviewItems() {
				// Remove any empty review items
				this.form.reviewItems = this.form.reviewItems.filter((x) => x.item != null);
			},
			submit() {
				this.$v.form.$touch();
				this.cleanReviewItems();

				if (this.$v.form.$invalid) {
					console.log('invalid form');
				} else {
					if (confirm('Are you sure you wish to save this Performance Review?')) {
						let postObj = {
							sendMeetingRequests: this.form.sendMeetingRequests,
							sendEmails: this.form.sendEmails,
							performanceReview: {
								id: this.form.id != undefined ? this.form.id : null,
								periodFromUTC: this.form.periodFromUTC.date,
								periodFromLocal: this.form.periodFromUTC.date,
								periodToUTC: this.form.periodToUTC.date,
								periodToLocal: this.form.periodToUTC.date,
								scheduledDateUTC: this.scheduledDateUtcFormatted,
								scheduledDateLocal: this.scheduledDateUtcFormatted,
								reviewer: {
									id: this.form.reviewer.id,
									firstName: this.form.reviewer.firstName,
									lastName: this.form.reviewer.lastName,
									fullName: this.form.reviewer.fullName,
									email: this.form.reviewer.email,
									reportsTo: this.form.reviewer.reportsTo != null ? this.form.reviewer.reportsTo.id : null,
									department: this.form.reviewer.department
								},
								employee: {
									id: this.form.employee.id,
									firstName: this.form.employee.firstName,
									lastName: this.form.employee.lastName,
									fullName: this.form.employee.fullName,
									email: this.form.employee.email,
									reportsTo: this.form.employee.reportsTo != null ? this.form.employee.reportsTo.id : null,
									department: this.form.employee.department
								},
								reviewItems: this.form.reviewItems,
								employeeSubmit: null,
								reviewerSubmit: null,
								status: 'Pending',
								files: [],
								meetingLocation: this.form.meetingLocation,
								scheduledDateTimeSpecified: this.scheduledDateTimeSpecified,
								templateId: this.form.performanceReviewTemplate != null ? this.form.performanceReviewTemplate.id : null,
								type: this.form.performanceReviewType,
								name: this.form.performanceReviewTemplate != null ? this.form.performanceReviewTemplate.name : 'Performance Review'
							}
						};
						this.$emit('save', postObj);
					}
				}
			},
			cancel() {
				this.$emit('cancel');
			},
			onAddItem() {
				this.itemDialogType = null;
				this.editedItemIndex = -1;
				this.editedItem = undefined;
				this.editItemDialog = true;

				// this.addPositionItemDialog = true;
			},
			onEditItem(item) {
				this.editedItemIndex = this.form.reviewItems.indexOf(item);
				this.editedItem = Object.assign({}, item);
				this.itemDialogType = this.editedItem.type;
				this.editItemDialog = true;
			},
			onDeleteItem(item) {
				const index = this.form.reviewItems.indexOf(item);
				confirm('Are you sure you want to delete this item?') && this.form.reviewItems.splice(index, 1);
			},
			saveItem(item) {
				if (this.editedItemIndex > -1) {
					this.form.reviewItems.splice(this.editedItemIndex, 1, item);
				} else {
					this.form.reviewItems.push(item);
				}
			},
			savePositionItemAddMulti(data) {
				for (let i = 0; i < data.items.length; i++) {
					this.form.reviewItems.push({
						id: null,
						category: data.category,
						item: data.items[i].item,
						outcomes: data.items[i].outcomes,
						type: data.type
					});
				}
			},
			closeItemEditor() {
				this.editItemDialog = false;
				this.addPositionItemDialog = false;
			},
			async onEmployeeSelect(employee) {
				this.selectedEmployee = employee;

				if (employee.reportsTo !== null) {
					const foundReviewer = this.employees.find((x) => x.id === employee.reportsTo);
					if (foundReviewer) {
						this.form.reviewer = foundReviewer;
					}
				}

				// We only want to bring the employee's PD in if we're dealing with the default type of PR
				if (this.form.performanceReviewType === 0) {
					this.$v.form.employee.$touch();

					await this.loadEmployeePD();

					this.populateReviewItemsFromEmployee();
				}
			},
			populateReviewItemsFromTemplate(template) {
				let itemsToDiscuss = [];

				itemsToDiscuss = template.reviewItems.map((item) => {
					return {
						id: this.newGuid(),
						category: item.category,
						item: item.item,
						type: item.type,
						outcomes: item.outcomes,
						group: item.group,
						rankType: item.rankType
					};
				});

				this.form.reviewItems = itemsToDiscuss;
			},
			async loadEmployeePD() {
				this.isLoadingEmployee = true;

				try {
					const employee = await this.$store.dispatch('employees/getEmployee', this.selectedEmployee.id);
					this.employeePositionUsedForReview = employee.currentPosition;
				} catch {
					this.isLoadingEmployee = false;
					this.showSnackbar('An error occurred loading employee Position Description. Please try or contact support', true);
				} finally {
					this.isLoadingEmployee = false;
				}
			},
			populateReviewItemsFromEmployee() {
				let itemsToDiscuss = [];

				if (this.employeePositionUsedForReview && this.employeePositionUsedForReview.positionItems) {
					
					const positionItemsToDiscuss = this.employeePositionUsedForReview.positionItems.filter(positionItem => {
						if (!positionItem.isPerformanceReviewDiscussionPoint) return false;
						if (positionItem.type === 1 && !this.performanceReviewOptions.includeKPIs) return false;
						if (positionItem.type === 2 && !this.performanceReviewOptions.includeKeyDuties) return false;
						if (positionItem.type === 5 && !this.performanceReviewOptions.includePersonSpecification) return false;

						return true;
					});
					
					if (positionItemsToDiscuss.length > 0) {
						const pdItemsMapped = positionItemsToDiscuss.map((item) => {
							return {
								id: null,
								category: item.category,
								item: item.item,
								type: item.type,
								outcomes: item.outcomes.filter((outcome) => outcome.isPerformanceReviewDiscussionPoint),
								group: this.getTypeFriendly(item.type, true),
								rankType: 1 // Traffic Light
							};
						});

						if (this.performanceReviewOptions.condensed == 0) {
							itemsToDiscuss = pdItemsMapped;
						} else {
							let kpis = pdItemsMapped.filter((reviewItem) => reviewItem.type === 1);
							let keyDuties = pdItemsMapped.filter((reviewItem) => reviewItem.type === 2);

							if (this.performanceReviewOptions.condenseKPIs) {
								kpis = this.condenseReviewItems(kpis);
							}

							keyDuties = this.condenseReviewItems(keyDuties);

							const otherItems = pdItemsMapped.filter((reviewItem) => ![1, 2].includes(reviewItem.type));

							itemsToDiscuss = [...kpis, ...keyDuties, ...otherItems];
						}
					}
				}

				// Add Company Values (if any)
				if (this.performanceReviewOptions.includeCoreValues && this.coreValues !== null) {
					for (let i = 0; i < this.coreValues.length; i++) {
						itemsToDiscuss.push({
							id: null,
							category: 'N/A',
							item: this.coreValues[i].text,
							type: 4,
							outcomes: [],
							group: this.getTypeFriendly(4, true),
							rankType: 1 // Traffic Light
						});
					}
				}

				// Add default 360 questions (if any)
				if (this.defaultDiscussionPoints !== null) {
					for (let i = 0; i < this.defaultDiscussionPoints.length; i++) {
						itemsToDiscuss.push({
							id: null,
							category: this.defaultDiscussionPoints[i].item.category,
							item: this.defaultDiscussionPoints[i].item.item,
							type: this.defaultDiscussionPoints[i].item.type,
							outcomes: this.defaultDiscussionPoints[i].item.outcomes,
							group: this.getTypeFriendly(this.defaultDiscussionPoints[i].item.type, true),
							rankType: 1 // Traffic Light
						});
					}
				}

				// sort items to discuss
				let sortOrder = [1, 2, 5, 0, 3, 4];
				let resultsOrdered = [];
				for (let i = 0; i < sortOrder.length; i++) {
					for (let x = 0; x < itemsToDiscuss.length; x++) {
						if (itemsToDiscuss[x].type == sortOrder[i]) {
							resultsOrdered.push(itemsToDiscuss[x]);
						}
					}
				}

				this.form.reviewItems = resultsOrdered;
			},
			condenseReviewItems(reviewItems) {
				return [
					...new Set(
						reviewItems.map((reviewItem) => {
							return JSON.stringify({
								category: reviewItem.category,
								group: reviewItem.group,
								type: reviewItem.type
							});
						})
					)
				]
					.map((condensedReviewItemJson) => {
						const parsed = JSON.parse(condensedReviewItemJson);
						return {
							category: 'N/A',
							group: parsed.group,
							id: null,
							item: parsed.category,
							outcomes: [],
							rankType: 1,
							type: parsed.type,
							isCondensed: true
						};
					})
					.filter((condensedReviewItem) => condensedReviewItem.item !== 'N/A');
			},
			onAddMeetingLocation() {
				this.meetingLocationEditorDialog = true;
			},
			saveMeetingLocation(meetingLocation) {
				this.$store
					.dispatch('meetingLocations/add', meetingLocation)
					.then(
						(response) => {
							this.meetingLocationAddedNotifText = 'New Meeting Location added';
							this.form.meetingLocation = response.data;
						},
						(error) => {
							this.meetingLocationAddedNotifText = 'Failed to add Meeting Location';
							console.error('Error saving Meeting Location');
						}
					)
					.finally(() => {
						this.meetingLocationAddedNotif = true;
					});
			},
			onPerformanceReviewOptionsUpdated(newOptions) {
				this.performanceReviewOptions = newOptions;
				this.showPerformanceReviewOptionsDialog = false;
			}
		}
	};
</script>

<style scoped>
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px !important;
		font-style: italic;
	}
	.v-radio {
		align-items: start;
	}
	.condense-switch {
		margin-top: 0;
	}
</style>
